import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import type { SpendingTrendChartProps, SpendingTrendDataPoint } from '../../types/analytics';
import { useThemeStore } from '../../store';

/**
 * Format a number as currency.
 */
function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date based on granularity.
 */
function formatDate(dateString: string, granularity: string = 'day'): string {
  try {
    const date = new Date(dateString);

    if (granularity === 'month') {
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else if (granularity === 'week') {
      return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  } catch {
    return dateString;
  }
}

/**
 * Custom tooltip component for the chart.
 */
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: SpendingTrendDataPoint }>;
  label?: string;
  granularity: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, granularity }) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-cap-dark p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
        <p className="text-sm font-medium text-gray-900 dark:text-warm-white">
          {formatDate(data.date, granularity)}
        </p>
        <p className="text-sm text-primary-600 font-semibold">
          {formatCurrency(data.amount)}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {data.count} {data.count === 1 ? 'purchase' : 'purchases'}
        </p>
      </div>
    );
  }
  return null;
};

/**
 * SpendingTrendChart Component
 *
 * Displays a line chart showing spending over time using recharts.
 * Supports different granularities (day, week, month) and click interactions.
 */
const SpendingTrendChart: React.FC<SpendingTrendChartProps> = ({
  data,
  loading,
  onDataPointClick,
}) => {
  const isDark = useThemeStore((s) => s.resolvedTheme === 'dark');
  const gridStroke = isDark ? '#374151' : '#E5E7EB';
  const tickFill = isDark ? '#9CA3AF' : '#6B7280';
  const dotStroke = isDark ? '#1A1A1A' : '#ffffff';

  // Loading state
  if (loading) {
    return (
      <div role="status" aria-busy="true" aria-label="Loading spending trend chart">
        <div
          data-testid="trend-chart-skeleton"
          className="bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4 h-64 animate-pulse"
        >
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
          <div className="h-48 bg-gray-100 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  // Empty or null data state
  if (!data || data.data_points.length === 0) {
    return (
      <div
        data-testid="spending-trend-chart"
        className="bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4 h-64 flex flex-col items-center justify-center"
      >
        <p className="text-gray-500 dark:text-gray-400 font-medium">No spending data</p>
        <p className="text-gray-400 text-sm mt-1">No purchases in selected period</p>
      </div>
    );
  }

  const { data_points, granularity, total_amount, total_count } = data;

  // Handle data point click
  const handleClick = (dataPoint: SpendingTrendDataPoint) => {
    if (onDataPointClick) {
      onDataPointClick(dataPoint.date);
    }
  };

  return (
    <div data-testid="spending-trend-chart" className="bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Spending Trend</h3>
        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          <span>Total: {formatCurrency(total_amount)}</span>
          <span>{total_count} {total_count === 1 ? 'purchase' : 'purchases'}</span>
        </div>
      </div>

      {/* Chart description for accessibility */}
      <div data-testid="chart-description" className="sr-only">
        Spending trend chart with {data_points.length} data points.
        Total spending: {formatCurrency(total_amount)}.
      </div>

      {/* Chart */}
      <div
        className="h-64"
        role="img"
        aria-label="Spending trend chart"
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data_points}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => formatDate(value, granularity)}
              tick={{ fontSize: 12, fill: tickFill }}
              stroke={gridStroke}
            />
            <YAxis
              tickFormatter={(value) => `$${value}`}
              tick={{ fontSize: 12, fill: tickFill }}
              stroke={gridStroke}
            />
            <Tooltip content={<CustomTooltip granularity={granularity} />} />
            <Line
              type="monotone"
              dataKey="amount"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={{
                r: 4,
                fill: '#3B82F6',
                stroke: dotStroke,
                strokeWidth: 2,
                cursor: onDataPointClick ? 'pointer' : 'default',
              }}
              activeDot={{
                r: 6,
                fill: '#2563EB',
                stroke: dotStroke,
                strokeWidth: 2,
                onClick: (_, event: any) => {
                  if (event && event.payload) {
                    handleClick(event.payload);
                  }
                },
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Data points for testing purposes (hidden) */}
      <div className="hidden">
        {data_points.map((point, index) => (
          <div
            key={point.date}
            data-testid={`data-point-${index}`}
            style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
            onClick={() => handleClick(point)}
          />
        ))}
      </div>
    </div>
  );
};

export default SpendingTrendChart;
